import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import {
  CurrencySchema,
  MoneySchema,
  SchemaValidationFailedError,
  SlugSchema,
} from '@kilat/shared';

/** Typed wrappers around billing-related Cloud Functions. */

// ── addOutlet ────────────────────────────────────────────────────────────

export const AddOutletInputSchema = z.object({
  tenantId: z.string().min(1),
  name: z.string().min(1).max(80),
  slug: z.string().optional(),
  address: z.string().max(500).default(''),
});
export type AddOutletInput = z.infer<typeof AddOutletInputSchema>;

export const AddOutletOutputSchema = z.object({
  outletId: z.string(),
  slug: SlugSchema,
  prorationAmount: MoneySchema,
  currency: CurrencySchema,
  day: z.number().int(),
  daysInMonth: z.number().int(),
  outletCountAfter: z.number().int(),
});
export type AddOutletOutput = z.infer<typeof AddOutletOutputSchema>;

const addOutletFn: HttpsCallable<AddOutletInput, AddOutletOutput> = httpsCallable(
  functions,
  'addOutlet',
);

export async function callAddOutlet(input: AddOutletInput): Promise<AddOutletOutput> {
  const parsed = AddOutletInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('addOutlet.input', parsed.error.flatten());
  }
  const result = await addOutletFn(parsed.data);
  const out = AddOutletOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('addOutlet.output', out.error.flatten());
  }
  return out.data;
}

// ── removeOutlet ─────────────────────────────────────────────────────────

export const RemoveOutletInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
});
export type RemoveOutletInput = z.infer<typeof RemoveOutletInputSchema>;

export const RemoveOutletOutputSchema = z.object({
  outletId: z.string(),
  effectiveAt: z.number().int().positive().describe('Millis-since-epoch'),
  outletCountAfterEffective: z.number().int().nonnegative(),
  eventId: z.string(),
});
export type RemoveOutletOutput = z.infer<typeof RemoveOutletOutputSchema>;

const removeOutletFn: HttpsCallable<RemoveOutletInput, RemoveOutletOutput> = httpsCallable(
  functions,
  'removeOutlet',
);

export async function callRemoveOutlet(input: RemoveOutletInput): Promise<RemoveOutletOutput> {
  const parsed = RemoveOutletInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('removeOutlet.input', parsed.error.flatten());
  }
  const result = await removeOutletFn(parsed.data);
  const out = RemoveOutletOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('removeOutlet.output', out.error.flatten());
  }
  return out.data;
}

// ── cancelOutletRemoval ──────────────────────────────────────────────────

export const CancelOutletRemovalInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  cancelledEventId: z.string().min(1),
});
export type CancelOutletRemovalInput = z.infer<typeof CancelOutletRemovalInputSchema>;

export const CancelOutletRemovalOutputSchema = z.object({
  outletId: z.string(),
  cancelledEventId: z.string(),
  restoredEventId: z.string(),
});
export type CancelOutletRemovalOutput = z.infer<typeof CancelOutletRemovalOutputSchema>;

const cancelOutletRemovalFn: HttpsCallable<CancelOutletRemovalInput, CancelOutletRemovalOutput> =
  httpsCallable(functions, 'cancelOutletRemoval');

export async function callCancelOutletRemoval(
  input: CancelOutletRemovalInput,
): Promise<CancelOutletRemovalOutput> {
  const parsed = CancelOutletRemovalInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('cancelOutletRemoval.input', parsed.error.flatten());
  }
  const result = await cancelOutletRemovalFn(parsed.data);
  const out = CancelOutletRemovalOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('cancelOutletRemoval.output', out.error.flatten());
  }
  return out.data;
}

// ── processOutletRemovalsNow (Sprint 24) ─────────────────────────────────

export const ProcessOutletRemovalsInputSchema = z.object({
  tenantId: z.string().min(1).optional(),
});
export type ProcessOutletRemovalsInput = z.infer<typeof ProcessOutletRemovalsInputSchema>;

export const ProcessOutletRemovalsOutputSchema = z.object({
  tenantsScanned: z.number().int().nonnegative(),
  archivedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  archived: z.array(
    z.object({
      tenantId: z.string(),
      outletId: z.string(),
      scheduledEventId: z.string(),
      outletCountAfter: z.number().int().nonnegative(),
    }),
  ),
  skipped: z.array(
    z.object({
      tenantId: z.string(),
      outletId: z.string(),
      reason: z.string(),
    }),
  ),
});
export type ProcessOutletRemovalsOutput = z.infer<typeof ProcessOutletRemovalsOutputSchema>;

const processOutletRemovalsFn: HttpsCallable<
  ProcessOutletRemovalsInput,
  ProcessOutletRemovalsOutput
> = httpsCallable(functions, 'processOutletRemovalsNow');

export async function callProcessOutletRemovalsNow(
  input: ProcessOutletRemovalsInput = {},
): Promise<ProcessOutletRemovalsOutput> {
  const parsed = ProcessOutletRemovalsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('processOutletRemovalsNow.input', parsed.error.flatten());
  }
  const result = await processOutletRemovalsFn(parsed.data);
  const out = ProcessOutletRemovalsOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('processOutletRemovalsNow.output', out.error.flatten());
  }
  return out.data;
}

// ── runMonthlyBillingNow (Sprint 26) ─────────────────────────────────────

export const RunMonthlyBillingInputSchema = z.object({
  tenantId: z.string().min(1).optional(),
  periodMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});
export type RunMonthlyBillingInput = z.infer<typeof RunMonthlyBillingInputSchema>;

export const RunMonthlyBillingOutputSchema = z.object({
  period: z.string(),
  tenantsScanned: z.number().int().nonnegative(),
  generatedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  erroredCount: z.number().int().nonnegative(),
  results: z.array(
    z.object({
      tenantId: z.string(),
      outcome: z.enum(['generated', 'skipped', 'error']),
      invoiceId: z.string().optional(),
      total: z.number().int().optional(),
      currency: z.string().optional(),
      lineCount: z.number().int().optional(),
      reason: z.string().optional(),
    }),
  ),
});
export type RunMonthlyBillingOutput = z.infer<typeof RunMonthlyBillingOutputSchema>;

const runMonthlyBillingFn: HttpsCallable<RunMonthlyBillingInput, RunMonthlyBillingOutput> =
  httpsCallable(functions, 'runMonthlyBillingNow');

export async function callRunMonthlyBillingNow(
  input: RunMonthlyBillingInput = {},
): Promise<RunMonthlyBillingOutput> {
  const parsed = RunMonthlyBillingInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('runMonthlyBillingNow.input', parsed.error.flatten());
  }
  const result = await runMonthlyBillingFn(parsed.data);
  const out = RunMonthlyBillingOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('runMonthlyBillingNow.output', out.error.flatten());
  }
  return out.data;
}

// ── generateInvoicePdf (Sprint 91) ──────────────────────────────────────

export const GenerateInvoicePdfInputSchema = z.object({
  tenantId: z.string().min(1),
  invoiceId: z.string().min(1),
});
export type GenerateInvoicePdfInput = z.infer<typeof GenerateInvoicePdfInputSchema>;

export const GenerateInvoicePdfOutputSchema = z.object({
  downloadURL: z.string().url(),
  objectPath: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  expiresAtMs: z.number().int().positive(),
});
export type GenerateInvoicePdfOutput = z.infer<typeof GenerateInvoicePdfOutputSchema>;

const generateInvoicePdfFn: HttpsCallable<GenerateInvoicePdfInput, GenerateInvoicePdfOutput> =
  httpsCallable(functions, 'generateInvoicePdf');

export async function callGenerateInvoicePdf(
  input: GenerateInvoicePdfInput,
): Promise<GenerateInvoicePdfOutput> {
  const parsed = GenerateInvoicePdfInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('generateInvoicePdf.input', parsed.error.flatten());
  }
  const result = await generateInvoicePdfFn(parsed.data);
  const out = GenerateInvoicePdfOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('generateInvoicePdf.output', out.error.flatten());
  }
  return out.data;
}

// ── setTenantTaxConfig (Sprint 91) ──────────────────────────────────────

export const SetTenantTaxConfigInputSchema = z.object({
  tenantId: z.string().min(1),
  registered: z.boolean(),
  sstNumber: z.string().max(40).optional(),
  rate: z.number().min(0).max(1).optional(),
  companyName: z.string().max(120).optional(),
  companyRegistration: z.string().max(40).optional(),
  registeredAddress: z.string().max(500).optional(),
});
export type SetTenantTaxConfigInput = z.infer<typeof SetTenantTaxConfigInputSchema>;

export const SetTenantTaxConfigOutputSchema = z.object({
  ok: z.literal(true),
  registered: z.boolean(),
  rate: z.number().optional(),
});
export type SetTenantTaxConfigOutput = z.infer<typeof SetTenantTaxConfigOutputSchema>;

const setTenantTaxConfigFn: HttpsCallable<SetTenantTaxConfigInput, SetTenantTaxConfigOutput> =
  httpsCallable(functions, 'setTenantTaxConfig');

export async function callSetTenantTaxConfig(
  input: SetTenantTaxConfigInput,
): Promise<SetTenantTaxConfigOutput> {
  const parsed = SetTenantTaxConfigInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('setTenantTaxConfig.input', parsed.error.flatten());
  }
  const result = await setTenantTaxConfigFn(parsed.data);
  const out = SetTenantTaxConfigOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('setTenantTaxConfig.output', out.error.flatten());
  }
  return out.data;
}

// ── setPayoutBankAccount ────────────────────────────────────────────────
//
// Owner-only write of `tenants/{tid}/private/payoutBank`. Client writes to that
// document are denied by firestore.rules; this callable is the only path.

export const SetPayoutBankAccountInputSchema = z.object({
  tenantId: z.string().min(1),
  bankName: z.string().max(80).optional(),
  accountName: z.string().max(100).optional(),
  accountNumber: z.string().max(40).optional(),
  duitNowId: z.string().max(40).optional(),
  thresholdSen: z.number().int().nonnegative(),
  autoWithdrawEnabled: z.boolean(),
});
export type SetPayoutBankAccountInput = z.infer<typeof SetPayoutBankAccountInputSchema>;

export const SetPayoutBankAccountOutputSchema = z.object({
  ok: z.literal(true),
});
export type SetPayoutBankAccountOutput = z.infer<typeof SetPayoutBankAccountOutputSchema>;

const setPayoutBankAccountFn: HttpsCallable<SetPayoutBankAccountInput, SetPayoutBankAccountOutput> =
  httpsCallable(functions, 'setPayoutBankAccount');

export async function callSetPayoutBankAccount(
  input: SetPayoutBankAccountInput,
): Promise<SetPayoutBankAccountOutput> {
  const parsed = SetPayoutBankAccountInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('setPayoutBankAccount.input', parsed.error.flatten());
  }
  const result = await setPayoutBankAccountFn(parsed.data);
  const out = SetPayoutBankAccountOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('setPayoutBankAccount.output', out.error.flatten());
  }
  return out.data;
}

// ── markInvoicePaidManually (ADR-0022 Option C) ─────────────────────────

export const MarkInvoicePaidManuallyInputSchema = z.object({
  brandId: z.string().min(1),
  tenantId: z.string().min(1),
  invoiceId: z.string().min(1),
  method: z.enum(['manual_bank_transfer', 'cash', 'waived']),
  reference: z.string().max(500).optional(),
});
export type MarkInvoicePaidManuallyInput = z.infer<typeof MarkInvoicePaidManuallyInputSchema>;

export const MarkInvoicePaidManuallyOutputSchema = z.object({
  ok: z.literal(true),
  alreadyPaid: z.boolean(),
  status: z.literal('paid'),
  totalSen: z.number().int().nonnegative(),
});
export type MarkInvoicePaidManuallyOutput = z.infer<typeof MarkInvoicePaidManuallyOutputSchema>;

const markInvoicePaidManuallyFn: HttpsCallable<
  MarkInvoicePaidManuallyInput,
  MarkInvoicePaidManuallyOutput
> = httpsCallable(functions, 'markInvoicePaidManually');

export async function callMarkInvoicePaidManually(
  input: MarkInvoicePaidManuallyInput,
): Promise<MarkInvoicePaidManuallyOutput> {
  const parsed = MarkInvoicePaidManuallyInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('markInvoicePaidManually.input', parsed.error.flatten());
  }
  const result = await markInvoicePaidManuallyFn(parsed.data);
  const out = MarkInvoicePaidManuallyOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('markInvoicePaidManually.output', out.error.flatten());
  }
  return out.data;
}

// ── retryToyyibPayBill (ADR-0022 Option B; contract inherited from the ──
// ── retired Sprint-44 retryBillplzBill) ─────────────────────────────────

export const RetryToyyibPayBillInputSchema = z.object({
  tenantId: z.string().min(1),
  invoiceId: z.string().min(1),
});
export type RetryToyyibPayBillInput = z.infer<typeof RetryToyyibPayBillInputSchema>;

export const RetryToyyibPayBillOutputSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    billId: z.string(),
    paymentUrl: z.string().url(),
  }),
  z.object({
    ok: z.literal(false),
    reason: z.enum([
      'already_paid',
      'invoice_closed',
      'amount_invalid',
      'email_missing',
      'secrets_missing',
      'gateway_failed',
    ]),
    message: z.string(),
  }),
]);
export type RetryToyyibPayBillOutput = z.infer<typeof RetryToyyibPayBillOutputSchema>;

const retryToyyibPayBillFn: HttpsCallable<RetryToyyibPayBillInput, RetryToyyibPayBillOutput> =
  httpsCallable(functions, 'retryToyyibPayBill');

export async function callRetryToyyibPayBill(
  input: RetryToyyibPayBillInput,
): Promise<RetryToyyibPayBillOutput> {
  const parsed = RetryToyyibPayBillInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('retryToyyibPayBill.input', parsed.error.flatten());
  }
  const result = await retryToyyibPayBillFn(parsed.data);
  const out = RetryToyyibPayBillOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('retryToyyibPayBill.output', out.error.flatten());
  }
  return out.data;
}
